import React, { ForwardedRef, Fragment, memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CartItem,
  CartLink,
  ConfirmationModal,
  ExpansionArrowButton,
  PopoverMenu,
  ViewportContext,
} from "@monorepo/components";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { convertNumberToCurrency, errorTitle, isResponseErrorArray } from "@monorepo/helpers";
import { SharedReceivedIcon } from "@monorepo/icons";
import {
  useCancelSharedCartMutation,
  useDeleteCartGroupMutation,
  useGetCartGroupsQuery,
  useGetCartsQuery,
} from "@monorepo/services/api/carts";
import theme from "@monorepo/styles/theme";
import { CartStatusesEnum, DelayStatusesEnum } from "@monorepo/types";
import { MoreVert } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, styled, Tooltip, Typography } from "@mui/material";
import { Popover } from "@mui/material";
import Big from "big.js";
import { reverse } from "named-urls";

type TModalActions = "confirm" | "error" | "cancel-share";
type TContextActions = "delete" | "edit" | "share" | "view-shared-clients";

interface IProps {
  cartGroupId: string;
  path: string;
  title: string;
  activeCartGroupId?: string;
  activeCartId?: string;
}

const StyledTitle = styled("div")(() => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
}));

export const CartGroupItem: React.FC<IProps> = memo(({ activeCartGroupId, cartGroupId, path, title, activeCartId }) => {
  const [showPopoverMenu, setShowPopoverMenu] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<TModalActions | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { isThirdParty } = useContext(ViewportContext);

  const { data } = useGetCartsQuery();
  const { data: cartGroups } = useGetCartGroupsQuery();
  const history = useHistory();
  const { t } = useTranslation();
  const [deleteCartGroup, { error: deleteError }] = useDeleteCartGroupMutation();
  const [cancelShareCart] = useCancelSharedCartMutation();

  const currentCartGroup = useMemo(
    () => cartGroups?.find((cartGroup) => cartGroup.id === cartGroupId),
    [cartGroupId, cartGroups]
  );

  const isActive = activeCartGroupId === cartGroupId && !activeCartId;
  const hasSharedClients = currentCartGroup?.is_owner && (currentCartGroup.shared_clients?.length || 0) > 0;
  const carts = useMemo(() => {
    return data?.filter((cart) => String(cart.cart_group_id) === String(cartGroupId)) || [];
  }, [data, cartGroupId]);

  const totalValue = useMemo(
    () =>
      carts
        .reduce((acc, curr) => {
          if (curr.delayed === DelayStatusesEnum.NON_DELAYED && curr.status_id === CartStatusesEnum.UNPAID) {
            return acc.plus(curr.sum);
          }
          return acc;
        }, new Big(0))
        .toFixed(2),
    [carts]
  );

  const cartsIds = useMemo(() => carts.map(({ id }) => id), [carts]);
  const isExpanded = activeCartId ? cartsIds.includes(activeCartId) : false;
  const [expanded, setExpanded] = useState<boolean>(isExpanded);
  const cartLinkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && cartLinkRef?.current) {
      if (!isThirdParty) {
        cartLinkRef?.current.scrollIntoView();
      }
      setExpanded(true);
    }
  }, [isExpanded, isThirdParty]);

  const handleClickMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setShowPopoverMenu(true);
  };

  const handleCloseMore = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShowPopoverMenu(false);
    setAnchorEl(null);
  };

  const handleContextAction = (action: TContextActions) => {
    setShowPopoverMenu(false);

    switch (action) {
      case "delete": {
        // if user is not the owner of the cart - cancel the shared group cart
        if (currentCartGroup?.is_owner !== undefined && currentCartGroup.is_owner === false) {
          setModalAction("cancel-share");
          break;
        }

        // show error modal if cartgroup has carts
        setModalAction(carts.length > 0 ? "error" : "confirm");
        break;
      }

      case "edit": {
        history.push(
          reverse(routeConfig.dashboard.payments.cartGroup.editCartGroup, {
            cartGroupId,
          })
        );
        break;
      }

      case "share": {
        ReactGA.event("cart_share");
        history.push(
          reverse(routeConfig.dashboard.payments.cartGroup.shareCartGroup, {
            cartGroupId,
          })
        );
        break;
      }

      case "view-shared-clients": {
        ReactGA.event("cart_share_list");
        history.push(
          reverse(routeConfig.dashboard.payments.cartGroup.sharedClients, {
            cartGroupId,
          })
        );
      }
    }
  };

  const handleCartGroupDelete = async () => {
    if (carts.length > 0) return;

    // this invalidate the getCartGroupById query, hence once refetching the data for the deleted cartGroup (resulting in 404 error)
    deleteCartGroup(cartGroupId)
      .unwrap()
      .then(() => {
        if (cartGroups?.length && String(cartGroupId) === String(activeCartGroupId)) {
          history.push(
            reverse(routeConfig.dashboard.payments.cartGroup.self, { cartGroupId: String(cartGroups[0].id) })
          );
        }
      });
  };

  const handleCartGroupCancelShare = async () => {
    cancelShareCart({ cart_group_id: cartGroupId })
      .unwrap()
      .then((res) => {
        if (cartGroups?.length && String(cartGroupId) === String(activeCartGroupId)) {
          history.push(
            reverse(routeConfig.dashboard.payments.cartGroup.self, { cartGroupId: String(cartGroups[0].id) })
          );
        }
        toast.success(res.message);
      })
      .catch((err) => {
        if (isResponseErrorArray(err)) {
          toast.error(errorTitle(err));
        }
      })
      .finally(() => setModalAction(null));
  };
  return (
    <Fragment key={cartGroupId}>
      <CartLink to={path} key={cartGroupId} isActive={isActive}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: currentCartGroup?.is_owner ? "40px 1.2fr 0.4fr 34px" : "40px 1.2fr 0.3fr 24px 34px",
            gridColumnGap: theme.spacing(1),
            alignItems: "center",
          }}
          ref={cartLinkRef}
        >
          {carts && carts.length > 0 ? (
            <ExpansionArrowButton
              sx={{ width: "40px", height: "40px", color: "inherit" }}
              expanded={expanded}
              onSetExpanded={() => {
                setExpanded(!expanded);
              }}
            />
          ) : (
            <Box />
          )}

          <StyledTitle
            sx={{
              fontSize: !!isThirdParty ? "unset" : "1.25rem",
            }}
            title={title}
          >
            {title}
          </StyledTitle>
          <Typography
            sx={{
              fontSize: !!isThirdParty ? "unset" : "1.25rem",
            }}
          >
            {carts?.length ? convertNumberToCurrency(totalValue) : null}
          </Typography>
          {!currentCartGroup?.is_owner && currentCartGroup?.owner_name && (
            <Tooltip title={t("cart.has_been_shared", { ownerName: currentCartGroup?.owner_name })}>
              <WrappedShareSentIcon />
            </Tooltip>
          )}
          <IconButton aria-label={t("common.show_actions")} size="small" onClick={handleClickMore}>
            <MoreVert sx={{ color: theme.palette.text.disabled }} />
          </IconButton>
          <Popover
            disableAutoFocus // This is responsible for auto scrolling in cart group item when opening PopoverMenu.
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            open={showPopoverMenu}
            anchorEl={anchorEl}
            onClose={handleCloseMore}
          >
            <PopoverMenu
              actionIcon={<EditIcon fontSize="small" />}
              onAction={currentCartGroup?.is_owner ? () => handleContextAction("edit") : null}
              onDelete={() => handleContextAction("delete")}
              onShare={!isThirdParty && currentCartGroup?.is_owner ? () => handleContextAction("share") : null}
              onClientsClick={
                !isThirdParty && hasSharedClients ? () => handleContextAction("view-shared-clients") : null
              }
              onClientsClickTooltipText="cart.action_tooltips.edit_shared"
              onActionTooltipText="cart.action_tooltips.edit"
              onDeleteTooltipText="cart.action_tooltips.delete"
              onShareTooltipText="cart.action_tooltips.share"
            />
          </Popover>
        </Box>
      </CartLink>
      {expanded && carts && carts.length > 0 && (
        <Box
          sx={{
            display: "grid",
            rowGap: theme.spacing(1),
            marginLeft: theme.spacing(1),
            marginTop: theme.spacing(1),
          }}
        >
          {carts.map(({ id, service_name: serviceName, sum, delayed, status_id: statusId }) => (
            <CartItem key={id} cartId={id} title={serviceName} delayed={delayed} statusId={statusId} sum={sum} />
          ))}
        </Box>
      )}

      {isResponseErrorArray(deleteError) && <Typography>{deleteError?.map((err) => err.title).join(" ")}</Typography>}
      {modalAction === "confirm" && (
        <ConfirmationModal
          open={modalAction === "confirm"}
          confirmButtonText={t("common.delete")}
          declineButtonText={t("common.cancel")}
          onAction={handleCartGroupDelete}
          onClose={() => setModalAction(null)}
        >
          <Trans defaults={t("cart.sure_to_delete_list")} components={{ bold: <strong /> }} />
        </ConfirmationModal>
      )}
      {modalAction === "error" && (
        <ConfirmationModal open={modalAction === "error"} onClose={() => setModalAction(null)}>
          <Trans defaults={t("cart.cannot_delete_list")} />
        </ConfirmationModal>
      )}
      {modalAction === "cancel-share" && (
        <ConfirmationModal
          open={modalAction === "cancel-share"}
          confirmButtonText={t("common.remove")}
          declineButtonText={t("common.cancel")}
          onAction={handleCartGroupCancelShare}
          onClose={() => setModalAction(null)}
        >
          <Trans defaults={t("cart.sure_to_cancel_shared_cart")} components={{ bold: <strong /> }} />
        </ConfirmationModal>
      )}
    </Fragment>
  );
});

const WrappedShareSentIcon = React.forwardRef(function WrappedShareSentIcon(props, ref: ForwardedRef<HTMLDivElement>) {
  return (
    <div {...props} ref={ref}>
      <SharedReceivedIcon />
    </div>
  );
});
